import React, { useState, useRef} from "react";
import {
  Microscope,
  X,
  Plus,
  Calendar,
  Save,
  Pencil,
  Trash2,
  Bot,
  Loader,
  Eye,
} from "lucide-react";
import { useSorologias } from "../context/SorologiasContext";
import { usePacientes } from "../../pacientes/context/PacientesContext";
import { useAuth } from "../../auth/context/AuthContext";
import {
  podeEscreverClinico,
  ehAdministrador,
  ehMedico,
} from "../../../shared/utils/permissoesCategoria";
import { Paciente } from "../../../types";
import { theme } from "../../../shared/styles/theme";
import Button from "../../../shared/components/ui/Button";
import CampoSorologia from "./CampoSorologia";
import {
  padronizarRegistroSorologia,
  camposNaoReconhecidos,
} from "../services/normalizarSorologia";
import { ehDataFutura, msgDataFutura, hojeISO } from "../../../shared/utils/dataNaoFutura";
import { gerarPromptSorologias } from "../../ia-analise/services/gerarPromptSorologias";
import { callAnalyze } from "../../../lib/analyzeClient";
import TextoAnaliseMarkdown from "../../../shared/components/TextoAnaliseMarkdown";

interface TabelaSorologiasProps {
  paciente: Paciente;
}

export default function TabelaSorologias({ paciente }: TabelaSorologiasProps) {
  const {
    listarSorologiasPorPaciente,
    adicionarSorologia,
    editarSorologia,
    removerSorologia,
  } = useSorologias();

  const sorologias = listarSorologiasPorPaciente(paciente.id);
  const { usuarioLogado } = useAuth();
  const { atualizarPaciente } = usePacientes();
  // 🔐 B88 (AI_98): nome legado - hoje significa "somente leitura".
  // Cobre convidado E observador (espelho de canWriteClinical das rules).
  const isConvidado = !podeEscreverClinico(usuarioLogado);
  // 🔐 B323 (AI_145): admin mantém editar/excluir após 24h (escape B296).
  const ehAdmin = ehAdministrador(usuarioLogado);
  // §6.3.2/B88 (AI_145): GERAÇÃO de IA é ato médico (só o botão "Análise IA");
  // "Ver última análise" (leitura) permanece à equipe sob !isConvidado.
  const podeGerarIA = ehMedico(usuarioLogado);

  // ========================================
  // 🤖 ANÁLISE DE IA DE SOROLOGIAS (B313/AI_141) — espelha o padrão de marcadores
  // ========================================
  const [mostrarAnaliseIA, setMostrarAnaliseIA] = useState(false);
  const [loadingIA, setLoadingIA] = useState(false);
  const [resultadoIA, setResultadoIA] = useState<string | null>(null);
  const [erroIA, setErroIA] = useState<string | null>(null);
  const [dataAnaliseExibida, setDataAnaliseExibida] = useState<string | null>(
    null
  );
  const [ultimaAnaliseLocal, setUltimaAnaliseLocal] = useState<{
    texto: string;
    geradaEm: string;
  } | null>(null);
  const ultimaAnaliseDisponivel =
    paciente.ultimaAnaliseSorologias || ultimaAnaliseLocal;

  const handleAnaliseIA = async () => {
    if (sorologias.length === 0) {
      alert("Adicione pelo menos uma coleta de sorologias para análise.");
      return;
    }
    const prompt = gerarPromptSorologias(paciente, sorologias);
    setMostrarAnaliseIA(true);
    setLoadingIA(true);
    setErroIA(null);
    setResultadoIA(null);
    setDataAnaliseExibida(null);
    try {
      const data = await callAnalyze<{
        content?: Array<{ type: string; text: string }>;
      }>({ prompt, useThinking: true });
      const textoResposta = data.content?.[0]?.text || "Sem resposta da IA";
      setResultadoIA(textoResposta);
      const agora = new Date().toISOString();
      setDataAnaliseExibida(agora);
      setUltimaAnaliseLocal({ texto: textoResposta, geradaEm: agora });
      // 💾 Persiste a última análise no paciente (durável) — soft-fail.
      try {
        await atualizarPaciente(paciente.id, {
          ultimaAnaliseSorologias: {
            texto: textoResposta,
            geradaEm: agora,
            geradaPor: usuarioLogado?.id || "",
            geradaPorNome: usuarioLogado?.nome || "",
          },
        });
      } catch (e) {
        console.warn(
          "⚠️ Não foi possível salvar a última análise de sorologias:",
          e
        );
      }
    } catch (error: any) {
      console.error("Erro na análise IA:", error);
      setErroIA(error.message || "Erro ao gerar análise");
    } finally {
      setLoadingIA(false);
    }
  };

  const verUltimaAnalise = () => {
    const ua = ultimaAnaliseDisponivel;
    if (!ua?.texto) return;
    setErroIA(null);
    setLoadingIA(false);
    setResultadoIA(ua.texto);
    setDataAnaliseExibida(ua.geradaEm || null);
    setMostrarAnaliseIA(true);
  };

  const fecharAnaliseIA = () => {
    setMostrarAnaliseIA(false);
    setResultadoIA(null);
    setErroIA(null);
    setDataAnaliseExibida(null);
  };

  const [modoEdicao, setModoEdicao] = useState(false);
  const [sorologiaEditando, setSorologiaEditando] = useState<string | null>(
    null
  );
  const [dadosEdicao, setDadosEdicao] = useState<any>({});
  const [novaColeta, setNovaColeta] = useState({
    dataColeta: "",
    hbsag: "",
    antiHBs: "",
    antiHBcIgG: "",
    antiHBcIgM: "",
    hbeag: "",
    antiHBe: "",
    genotipagemVHB: "",
    pcrVHB: "",
    antiHCV: "",
    hcvRNA: "",
    antiHAVIgG: "",
    antiHAVIgM: "",
    antiHDV: "",
    pcrVHD: "",
    antiHEV: "",
    cmvIgG: "",
    cmvIgM: "",
    cmvPCR: "",
    ebvIgG: "",
    ebvIgM: "",
    ebvPCR: "",
    rubeolaIgG: "",
    rubeolaIgM: "",
    antiHIV: "",
    vdrl: "",
    ftaAbs: "",
    toxoIgG: "",
    toxoIgM: "",
    chagasIgG: "",
    chagasIgM: "",
    chagas: "",
    htlv: "",
    estrongiloidesIgG: "",
    igraTB: "",
    ppd: "",
  });

  const exames = [
    // Hepatite B
    { key: "hbsag", label: "HBsAg", ref: "Negativo", grupo: "Hepatite B" },
    {
      key: "antiHBs",
      label: "Anti-HBs",
      ref: "> 10 mUI/mL",
      grupo: "Hepatite B",
    },
    {
      key: "antiHBcIgG",
      label: "Anti-HBc IgG",
      ref: "Negativo",
      grupo: "Hepatite B",
    },
    {
      key: "antiHBcIgM",
      label: "Anti-HBc IgM",
      ref: "Negativo",
      grupo: "Hepatite B",
    },
    { key: "hbeag", label: "HBeAg", ref: "Negativo", grupo: "Hepatite B" },
    { key: "antiHBe", label: "Anti-HBe", ref: "Negativo", grupo: "Hepatite B" },
    {
      key: "genotipagemVHB",
      label: "Genotipagem VHB",
      ref: "-",
      grupo: "Hepatite B",
    },
    {
      key: "pcrVHB",
      label: "PCR VHB",
      ref: "Indetectável",
      grupo: "Hepatite B",
    },

    // Hepatite C
    { key: "antiHCV", label: "Anti-HCV", ref: "Negativo", grupo: "Hepatite C" },
    {
      key: "hcvRNA",
      label: "HCV-RNA",
      ref: "Indetectável",
      grupo: "Hepatite C",
    },

    // Hepatite A
    {
      key: "antiHAVIgG",
      label: "Anti-HAV IgG",
      ref: "Negativo",
      grupo: "Hepatite A",
    },
    {
      key: "antiHAVIgM",
      label: "Anti-HAV IgM",
      ref: "Negativo",
      grupo: "Hepatite A",
    },

    // Hepatite D e E
    {
      key: "antiHDV",
      label: "Anti-HDV",
      ref: "Negativo",
      grupo: "Hepatite D/E",
    },
    {
      key: "pcrVHD",
      label: "PCR VHD",
      ref: "Indetectável",
      grupo: "Hepatite D/E",
    },
    {
      key: "antiHEV",
      label: "Anti-HEV",
      ref: "Negativo",
      grupo: "Hepatite D/E",
    },

    // CMV
    { key: "cmvIgG", label: "CMV IgG", ref: "Negativo", grupo: "CMV" },
    { key: "cmvIgM", label: "CMV IgM", ref: "Negativo", grupo: "CMV" },
    { key: "cmvPCR", label: "CMV-PCR", ref: "Indetectável", grupo: "CMV" },

    // EBV
    { key: "ebvIgG", label: "EBV IgG", ref: "Negativo", grupo: "EBV" },
    { key: "ebvIgM", label: "EBV IgM", ref: "Negativo", grupo: "EBV" },
    { key: "ebvPCR", label: "EBV-PCR", ref: "Indetectável", grupo: "EBV" },

    // Rubéola
    {
      key: "rubeolaIgG",
      label: "Rubéola IgG",
      ref: "Negativo",
      grupo: "Rubéola",
    },
    {
      key: "rubeolaIgM",
      label: "Rubéola IgM",
      ref: "Negativo",
      grupo: "Rubéola",
    },

    // Outros
    { key: "antiHIV", label: "Anti-HIV", ref: "Negativo", grupo: "Outros" },
    { key: "vdrl", label: "VDRL", ref: "Não reagente", grupo: "Outros" },
    { key: "ftaAbs", label: "FTA-ABS", ref: "Não reagente", grupo: "Outros" },
    { key: "toxoIgG", label: "Toxo IgG", ref: "Negativo", grupo: "Outros" },
    { key: "toxoIgM", label: "Toxo IgM", ref: "Negativo", grupo: "Outros" },
    { key: "chagasIgG", label: "Chagas IgG", ref: "Não reagente", grupo: "Outros" },
    { key: "chagasIgM", label: "Chagas IgM", ref: "Não reagente", grupo: "Outros" },
    { key: "chagas", label: "Chagas (geral)", ref: "Não reagente", grupo: "Outros" },
    { key: "htlv", label: "HTLV I/II", ref: "Negativo", grupo: "Outros" },

    // Rastreio pré-Tx — parasitas / tuberculose (AI_118)
    {
      key: "estrongiloidesIgG",
      label: "Estrongiloides IgG",
      ref: "Não reagente",
      grupo: "Tuberculose / Estrongiloidíase",
    },
    {
      key: "igraTB",
      label: "IGRA (Quantiferon-TB)",
      ref: "Negativo",
      grupo: "Tuberculose / Estrongiloidíase",
    },
    {
      key: "ppd",
      label: "PPD (tuberculina)",
      ref: "< 5 mm",
      grupo: "Tuberculose / Estrongiloidíase",
    },
  ];

  const adicionarNovaColeta = () => {
    if (!novaColeta.dataColeta) {
      alert("Por favor, informe a data da coleta!");
      return;
    }
    // 📅 B296 — data da coleta não pode ser futura (permite hoje/retroativo).
    if (ehDataFutura(novaColeta.dataColeta)) {
      alert(msgDataFutura("Data da coleta"));
      return;
    }
    // Verificar se pelo menos 1 resultado foi preenchido
    const camposSorologia = Object.entries(novaColeta).filter(
      ([key]) => key !== "dataColeta"
    );
    const temDados = camposSorologia.some(
      ([, valor]) => valor && String(valor).trim() !== ""
    );
    if (!temDados) {
      alert("Digite ao menos 1 resultado de sorologia para salvar.");
      return;
    }
    // B338-FU (AI_150) — mesma guarda do salvarEdicao: texto não reconhecido é
    // descartado no gravar, e sem aviso o médico acha que salvou.
    const naoLidosNovo = camposNaoReconhecidos(novaColeta as any);
    if (naoLidosNovo.length) {
      const seguir = window.confirm(
        `Não consegui interpretar o que está escrito em: ${naoLidosNovo.join(", ")}.\n\n` +
          `Se continuar, esses campos ficarão em branco no registro.\n\nDeseja salvar assim mesmo?`
      );
      if (!seguir) return;
    }
    // B247: padroniza os resultados no momento de gravar (fonte única).
    adicionarSorologia(
      paciente.id,
      padronizarRegistroSorologia({
        pacienteId: paciente.id,
        ...novaColeta,
      })
    );

    setNovaColeta({
      dataColeta: "",
      hbsag: "",
      antiHBs: "",
      antiHBcIgG: "",
      antiHBcIgM: "",
      hbeag: "",
      antiHBe: "",
      genotipagemVHB: "",
      pcrVHB: "",
      antiHCV: "",
      hcvRNA: "",
      antiHAVIgG: "",
      antiHAVIgM: "",
      antiHDV: "",
      pcrVHD: "",
      antiHEV: "",
      cmvIgG: "",
      cmvIgM: "",
      cmvPCR: "",
      ebvIgG: "",
      ebvIgM: "",
      ebvPCR: "",
      rubeolaIgG: "",
      rubeolaIgM: "",
      antiHIV: "",
      vdrl: "",
      ftaAbs: "",
      toxoIgG: "",
      toxoIgM: "",
      chagasIgG: "",
      chagasIgM: "",
      chagas: "",
      htlv: "",
      estrongiloidesIgG: "",
      igraTB: "",
      ppd: "",
    });
    setModoEdicao(false);
  };

  // B338-FU (AI_150) — ver TabelaMarcadores: o aviso cobra só o que mudou.
  const originalEdicaoRef = useRef<Record<string, any>>({});

  const iniciarEdicao = (sorologia: any) => {
    setSorologiaEditando(sorologia.id);
    setDadosEdicao({ ...sorologia });
    originalEdicaoRef.current = { ...sorologia };
  };

  const cancelarEdicao = () => {
    setSorologiaEditando(null);
    setDadosEdicao({});
  };

  const salvarEdicao = () => {
    if (sorologiaEditando) {
      // 📅 B296 — guarda contra data futura também na edição.
      if (ehDataFutura((dadosEdicao as any).dataColeta)) {
        alert(msgDataFutura("Data da coleta"));
        return;
      }
      // B338-FU (AI_150) — texto não reconhecido some em silêncio ao gravar (vira
      // undefined e é descartado): o médico não perceberia. Pergunta antes.
      const naoLidos = camposNaoReconhecidos(
        dadosEdicao as any,
        {},
        originalEdicaoRef.current
      );
      if (naoLidos.length) {
        const seguir = window.confirm(
          `Não consegui interpretar o que está escrito em: ${naoLidos.join(", ")}.\n\n` +
            `Se continuar, esses campos NÃO serão alterados (o valor anterior permanece). ` +
            `Para apagar um campo, deixe-o em branco.\n\nDeseja salvar assim mesmo?`
        );
        if (!seguir) return;
      }
      // B247: padroniza no gravar (inclusive normaliza valores antigos editados).
      editarSorologia(sorologiaEditando, padronizarRegistroSorologia(dadosEdicao));
      setSorologiaEditando(null);
      setDadosEdicao({});
    }
  };

  const formatarData = (dataString: string): string => {
    const [ano, mes, dia] = dataString.split("-");
    return `${dia}/${mes}/${ano}`;
  };

  const grupos = Array.from(new Set(exames.map((e) => e.grupo)));

  return (
    <div
      style={{
        background: "white",
        borderRadius: theme.borderRadius.xl,
        boxShadow: theme.shadows.md,
        padding: "16px",
        // ❌ REMOVER maxHeight e overflowY
      }}
    >
      {/* HEADER */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: "12px",
        }}
      >
        <h2
          style={{
            fontSize: "16px",
            fontWeight: "600",
            color: theme.gray[800],
            display: "flex",
            alignItems: "center",
            gap: "6px",
            fontFamily: theme.fonts.sans,
            margin: 0,
          }}
        >
          <Microscope size={18} style={{ marginRight: "8px" }} /> Sorologias
          Virais
        </h2>
        {!isConvidado && (
          <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
            {/* 🤖 Análise IA de sorologias (B313/AI_141) — §6.3.2: gerar é ato médico */}
            {podeGerarIA && (
            <button
              onClick={handleAnaliseIA}
              disabled={loadingIA || sorologias.length === 0}
              style={{
                padding: "10px 20px",
                background: loadingIA
                  ? theme.gray[400]
                  : "linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)",
                color: "white",
                border: "none",
                borderRadius: theme.borderRadius.lg,
                fontSize: "14px",
                fontWeight: "600",
                cursor:
                  loadingIA || sorologias.length === 0
                    ? "not-allowed"
                    : "pointer",
                fontFamily: theme.fonts.sans,
                display: "flex",
                alignItems: "center",
                gap: "8px",
                opacity: sorologias.length === 0 ? 0.5 : 1,
              }}
            >
              {loadingIA ? (
                <>
                  <Loader size={14} style={{ marginRight: "4px" }} />
                  Analisando...
                </>
              ) : (
                <>
                  <Bot size={14} style={{ marginRight: "4px" }} />
                  Análise IA
                </>
              )}
            </button>
            )}

            {ultimaAnaliseDisponivel?.texto && (
              <button
                onClick={verUltimaAnalise}
                disabled={loadingIA}
                style={{
                  padding: "10px 20px",
                  background: "white",
                  color: theme.primary[700],
                  border: `1px solid ${theme.primary[300]}`,
                  borderRadius: theme.borderRadius.lg,
                  fontSize: "14px",
                  fontWeight: "600",
                  cursor: loadingIA ? "not-allowed" : "pointer",
                  fontFamily: theme.fonts.sans,
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  opacity: loadingIA ? 0.5 : 1,
                }}
                title="Reabrir a última análise gerada (não gera uma nova)"
              >
                <Eye size={14} style={{ marginRight: "4px" }} />
                Ver última análise
              </button>
            )}

            <Button onClick={() => setModoEdicao(!modoEdicao)}>
              {modoEdicao ? (
                <>
                  <X size={14} style={{ marginRight: "6px" }} />
                  Cancelar
                </>
              ) : (
                <>
                  <Plus size={14} style={{ marginRight: "6px" }} />
                  Nova Coleta
                </>
              )}
            </Button>
          </div>
        )}
      </div>

      {/* FORMULÁRIO NOVA COLETA */}
      {modoEdicao && (
        <div
          style={{
            background: theme.gray[50],
            padding: "16px",
            borderRadius: theme.borderRadius.lg,
            marginBottom: "16px",
            maxHeight: "calc(100vh - 400px)",
            overflowY: "auto",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              marginBottom: "16px",
            }}
          >
            <label
              style={{
                fontWeight: "600",
                minWidth: "120px",
                fontFamily: theme.fonts.sans,
              }}
            >
              <Calendar size={14} style={{ marginRight: "6px" }} /> Data da
              Coleta:
            </label>
            <input
              type="date"
              max={hojeISO()}
              value={novaColeta.dataColeta}
              onChange={(e) =>
                setNovaColeta({ ...novaColeta, dataColeta: e.target.value })
              }
              style={{
                padding: "8px",
                border: `1px solid ${theme.gray[300]}`,
                borderRadius: theme.borderRadius.md,
                fontSize: "14px",
                fontFamily: theme.fonts.sans,
              }}
            />
            <Button onClick={adicionarNovaColeta}>
              <Save size={14} style={{ marginRight: "6px" }} /> Salvar Coleta
            </Button>
          </div>

          {/* EXAMES AGRUPADOS */}
          {grupos.map((grupo) => (
            <div key={grupo} style={{ marginBottom: "20px" }}>
              <h4
                style={{
                  fontSize: "14px",
                  fontWeight: "600",
                  color: theme.primary[700],
                  marginBottom: "12px",
                  borderBottom: `2px solid ${theme.primary[200]}`,
                  paddingBottom: "4px",
                  fontFamily: theme.fonts.sans,
                }}
              >
                {grupo}
              </h4>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(6, 1fr)",
                  gap: "20px",
                }}
              >
                {exames
                  .filter((e) => e.grupo === grupo)
                  .map((exame) => (
                    <div key={exame.key}>
                      <label
                        style={{
                          fontSize: "12px",
                          color: theme.gray[600],
                          display: "block",
                          marginBottom: "4px",
                          fontFamily: theme.fonts.sans,
                        }}
                      >
                        {exame.label}
                      </label>
                      <CampoSorologia
                        campo={exame.key}
                        valor={novaColeta[exame.key as keyof typeof novaColeta]}
                        onChange={(v) =>
                          setNovaColeta({ ...novaColeta, [exame.key]: v })
                        }
                      />
                    </div>
                  ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* TABELA */}
      {!modoEdicao && (
        <div
          style={{
            maxHeight: "calc(100vh - 350px)",
            overflowY: "auto",
            overflowX: "auto",
            border: `1px solid ${theme.gray[200]}`,
            borderRadius: theme.borderRadius.lg,
          }}
        >
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              fontSize: "14px",
              fontFamily: theme.fonts.sans,
            }}
          >
            <thead>
              <tr style={{ background: theme.gray[100] }}>
                <th
                  style={{
                    padding: "12px",
                    textAlign: "left",
                    fontWeight: "600",
                    fontFamily: theme.fonts.sans,
                    position: "sticky",
                    left: 0,
                    background: theme.gray[100],
                    zIndex: 2,
                    borderRight: `2px solid ${theme.gray[300]}`,
                    minWidth: "200px",
                  }}
                >
                  Exame
                </th>
                <th
                  style={{
                    padding: "12px",
                    textAlign: "left",
                    fontWeight: "600",
                    fontFamily: theme.fonts.sans,
                    minWidth: "150px",
                    borderRight: `1px solid ${theme.gray[300]}`,
                  }}
                >
                  Valor de Referência
                </th>
                {sorologias.map((sorologia) => (
                  <th
                    key={sorologia.id}
                    style={{
                      padding: "12px",
                      textAlign: "center",
                      fontWeight: "600",
                      fontFamily: theme.fonts.sans,
                      minWidth: "120px",
                      borderLeft: `1px solid ${theme.gray[300]}`,
                    }}
                  >
                    <div>
                      <div>{formatarData(sorologia.dataColeta)}</div>
                      <div style={{ marginTop: "8px" }}>
                        {!isConvidado && (
                          <>
                            {sorologiaEditando === sorologia.id ? (
                              <div
                                style={{
                                  display: "flex",
                                  gap: "4px",
                                  justifyContent: "center",
                                }}
                              >
                                <button
                                  onClick={salvarEdicao}
                                  style={{
                                    padding: "4px 8px",
                                    fontSize: "11px",
                                    background: theme.primary[600],
                                    color: "white",
                                    border: "none",
                                    borderRadius: theme.borderRadius.md,
                                    cursor: "pointer",
                                    fontFamily: theme.fonts.sans,
                                  }}
                                >
                                  <Save
                                    size={14}
                                    style={{ marginRight: "6px" }}
                                  />{" "}
                                  Salvar
                                </button>
                                <button
                                  onClick={cancelarEdicao}
                                  style={{
                                    padding: "4px 8px",
                                    fontSize: "11px",
                                    background: theme.gray[400],
                                    color: "white",
                                    border: "none",
                                    borderRadius: theme.borderRadius.md,
                                    cursor: "pointer",
                                    fontFamily: theme.fonts.sans,
                                  }}
                                >
                                  <X size={14} />
                                </button>
                              </div>
                            ) : (
                              (() => {
                                const criadoEm = sorologia.criadoEm;
                                const horasDecorridas = criadoEm
                                  ? (new Date().getTime() -
                                      new Date(criadoEm).getTime()) /
                                    (1000 * 60 * 60)
                                  : 0;
                                // B323 (AI_145, revisão): strict p/ espelhar a
                                // regra — sem criadoEm válido = travado p/ equipe
                                // (só admin), senão mostraria botão que a regra
                                // nega ("permissão negada").
                                const dentroJanela =
                                  ehAdmin || (!!criadoEm && horasDecorridas < 24);
                                if (!dentroJanela)
                                  return (
                                    <span
                                      style={{
                                        fontSize: "10px",
                                        color: theme.gray[400],
                                        fontStyle: "italic",
                                        fontFamily: theme.fonts.sans,
                                      }}
                                    >
                                      bloqueado (&gt;24h)
                                    </span>
                                  );
                                return (
                                  <div
                                    style={{
                                      display: "flex",
                                      gap: "4px",
                                      justifyContent: "center",
                                    }}
                                  >
                                    <button
                                      onClick={() => iniciarEdicao(sorologia)}
                                      style={{
                                        padding: "4px 8px",
                                        fontSize: "11px",
                                        background: theme.primary[100],
                                        color: theme.primary[700],
                                        border: `1px solid ${theme.primary[300]}`,
                                        borderRadius: theme.borderRadius.md,
                                        cursor: "pointer",
                                        fontFamily: theme.fonts.sans,
                                      }}
                                    >
                                      <Pencil size={14} />
                                    </button>
                                    <button
                                      onClick={() => {
                                        if (
                                          window.confirm(
                                            "Remover esta coleta de sorologias?\n\nEsta ação não pode ser desfeita."
                                          )
                                        ) {
                                          removerSorologia(sorologia.id);
                                        }
                                      }}
                                      style={{
                                        padding: "4px 6px",
                                        fontSize: "11px",
                                        background: "#fee2e2",
                                        color: "#dc2626",
                                        border: "1px solid #fecaca",
                                        borderRadius: theme.borderRadius.md,
                                        cursor: "pointer",
                                      }}
                                      title="Remover coleta"
                                    >
                                      <Trash2 size={14} />
                                    </button>
                                  </div>
                                );
                              })()
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {exames.map((exame, index) => (
                <tr
                  key={exame.key}
                  style={{
                    background: index % 2 === 0 ? "white" : theme.gray[50],
                  }}
                >
                  <td
                    style={{
                      padding: "12px",
                      fontWeight: "600",
                      fontFamily: theme.fonts.sans,
                      position: "sticky",
                      left: 0,
                      background: index % 2 === 0 ? "white" : theme.gray[50],
                      zIndex: 1,
                      borderRight: `2px solid ${theme.gray[300]}`,
                    }}
                  >
                    {exame.label}
                  </td>
                  <td
                    style={{
                      padding: "12px",
                      color: theme.gray[600],
                      fontSize: "12px",
                      fontFamily: theme.fonts.sans,
                      borderRight: `1px solid ${theme.gray[300]}`,
                    }}
                  >
                    {exame.ref}
                  </td>
                  {sorologias.map((sorologia) => {
                    const valor =
                      sorologia[exame.key as keyof typeof sorologia] || "";
                    const estaEditando = sorologiaEditando === sorologia.id;

                    return (
                      <td
                        key={sorologia.id}
                        style={{
                          padding: "12px",
                          textAlign: "center",
                          fontFamily: theme.fonts.sans,
                          borderLeft: `1px solid ${theme.gray[200]}`,
                        }}
                      >
                        {estaEditando ? (
                          <CampoSorologia
                            campo={exame.key}
                            valor={dadosEdicao[exame.key] || ""}
                            onChange={(v) =>
                              setDadosEdicao({ ...dadosEdicao, [exame.key]: v })
                            }
                          />
                        ) : (
                          valor || "-"
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {sorologias.length === 0 && !modoEdicao && (
        <div
          style={{
            textAlign: "center",
            padding: "48px",
            color: theme.gray[500],
            fontFamily: theme.fonts.sans,
          }}
        >
          <p>Nenhuma coleta registrada ainda.</p>
          <p style={{ fontSize: "12px", marginTop: "8px" }}>
            Clique em "Nova Coleta" para adicionar.
          </p>
        </div>
      )}

      {/* 🤖 MODAL DA ANÁLISE DE IA DE SOROLOGIAS (B313/AI_141) — mesmo padrão
          visual de marcadores/imagem. NÃO fecha ao clicar fora (só no X/Fechar). */}
      {mostrarAnaliseIA && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0, 0, 0, 0.7)",
            zIndex: 10000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "20px",
          }}
        >
          <div
            style={{
              background: "white",
              borderRadius: theme.borderRadius.xl,
              width: "100%",
              maxWidth: "900px",
              maxHeight: "90vh",
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
              boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
            }}
          >
            {/* Header */}
            <div
              style={{
                padding: "20px 24px",
                background: "linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)",
                color: "white",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <div>
                <h2
                  style={{
                    margin: 0,
                    fontSize: "18px",
                    fontWeight: "600",
                    fontFamily: theme.fonts.sans,
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                  }}
                >
                  🧫 Análise Inteligente de Sorologias
                </h2>
                {dataAnaliseExibida && (
                  <span
                    style={{
                      fontSize: "12px",
                      opacity: 0.85,
                      fontFamily: theme.fonts.sans,
                    }}
                  >
                    Gerada em{" "}
                    {new Date(dataAnaliseExibida).toLocaleString("pt-BR", {
                      day: "2-digit",
                      month: "2-digit",
                      year: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </span>
                )}
              </div>
              <button
                onClick={fecharAnaliseIA}
                style={{
                  background: "rgba(255, 255, 255, 0.2)",
                  border: "none",
                  color: "white",
                  width: "36px",
                  height: "36px",
                  borderRadius: "50%",
                  cursor: "pointer",
                  fontSize: "18px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                ✕
              </button>
            </div>

            {/* Corpo */}
            <div
              style={{
                padding: "24px",
                overflowY: "auto",
                flex: 1,
              }}
            >
              {loadingIA && (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: "12px",
                    padding: "40px",
                    color: theme.gray[600],
                    fontFamily: theme.fonts.sans,
                  }}
                >
                  <Loader size={32} className="animate-spin" />
                  <p>Analisando as sorologias…</p>
                </div>
              )}
              {erroIA && !loadingIA && (
                <div
                  style={{
                    background: "#fef2f2",
                    border: "1px solid #fecaca",
                    borderRadius: theme.borderRadius.md,
                    padding: "16px",
                    color: "#dc2626",
                    fontFamily: theme.fonts.sans,
                  }}
                >
                  {erroIA}
                </div>
              )}
              {resultadoIA && !loadingIA && (
                <TextoAnaliseMarkdown texto={resultadoIA} />
              )}
            </div>

            {/* Footer */}
            {!loadingIA && (
              <div
                style={{
                  padding: "16px 24px",
                  borderTop: `1px solid ${theme.gray[200]}`,
                  display: "flex",
                  justifyContent: "flex-end",
                }}
              >
                <button
                  onClick={fecharAnaliseIA}
                  style={{
                    padding: "10px 24px",
                    background: theme.gray[100],
                    color: theme.gray[700],
                    border: "none",
                    borderRadius: theme.borderRadius.lg,
                    fontSize: "14px",
                    fontWeight: "600",
                    cursor: "pointer",
                    fontFamily: theme.fonts.sans,
                  }}
                >
                  Fechar
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
